import { motion } from "framer-motion";
import { AlertTriangle, Thermometer, Droplets, Power, BarChart3, Ruler, Layers, Flame, Wind, Gauge, Plane } from "lucide-react";
import { Link } from "react-router-dom";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import heroImg from "@/assets/acclimation-hero.webp";
import usePageSEO from "@/hooks/usePageSEO";
import RelatedServices from "@/components/RelatedServices";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7 },
};

const EnvironmentAcclimation = () => {
  usePageSEO({
    title: "How Long Does Hardwood Flooring Need to Acclimate? Guide",
    description: "Hardwood floor acclimation guide — how long, EMC moisture science, species-calibrated meters, climate considerations for Michigan homes.",
    path: "/environment-acclimation",
  });

  return (
  <>
    <UtilBar />
    <SiteHeader />
    <main>

    {/* ══ 1 · HERO ══ */}
    <section className="relative bg-foreground min-h-[70vh] flex items-center overflow-hidden">
      <PageBreadcrumb variant="overlay" items={[
        { label: "Home", to: "/" },
        { label: "Services" },
        { label: "Environment & Acclimation" },
      ]} />
      <img width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
        src={heroImg}
        alt="Moisture meter resting on a wood plank"
        className="absolute inset-0 w-full h-full object-cover opacity-30"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/80 to-transparent" />
      <div className="relative z-10 max-w-[1200px] mx-auto px-5 md:px-12 py-28 md:py-36">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4 flex items-center gap-2">
            <span className="w-7 h-px bg-primary inline-block" />
            NWFA Certified Process
          </p>
          <h1 className="font-display text-[clamp(32px,5.5vw,60px)] font-semibold text-primary-foreground leading-[1.05] mb-6">
            The Real Science of Hardwood Floor Acclimation
          </h1>
          <p className="text-[14px] md:text-[16px] font-light text-primary-foreground/55 leading-[1.85] max-w-xl mb-8">
            Wood is a living, breathing material. Discover how we use digital moisture testing and HVAC science to ensure your floor survives Michigan's extreme seasonal changes.
          </p>
          <QuoteModal
            trigger={
              <button className="bg-primary text-primary-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors">
                Consult an NWFA Inspector
              </button>
            }
          />
        </motion.div>
      </div>
    </section>

    {/* ══ 2 · THE ACCLIMATION MYTH ══ */}
    <section className="bg-foreground py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div
          {...fadeUp}
          className="border border-[hsl(var(--gold-300)/0.3)] bg-primary/[.04] rounded-2xl p-8 md:p-12 max-w-4xl mx-auto"
        >
          <div className="flex items-start gap-4 mb-5">
            <AlertTriangle className="w-7 h-7 text-[hsl(var(--gold-300))] shrink-0 mt-1" strokeWidth={1.5} />
            <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary">
              Industry Misconception
            </p>
          </div>
          <h2 className="font-display text-[clamp(24px,4vw,42px)] font-semibold text-primary-foreground leading-[1.1] mb-5">
            Shattering the Acclimation Myth:{" "}
            <em className="italic font-normal text-[hsl(var(--gold-300))]">It's Not About the Clock.</em>
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/55 leading-[1.9]">
            One of the biggest misconceptions in flooring is that acclimation is strictly a time-related process. Some contractors just drop wood in a room for three days and assume it's ready. While you can occasionally get lucky and have wood delivered that already matches your home's environment, true acclimation is entirely about adjusting the wood's internal moisture to match its new ecosystem. It is a science of <strong className="text-[hsl(var(--gold-300))] font-medium">measurement</strong>, not a calendar. <Link to="/reclaimed-barnwood" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">Century-old reclaimed material</Link> carries a fundamentally different moisture history than kiln-dried new hardwood, and requires an extended acclimation window that most contractors underestimate.
          </p>
        </motion.div>
      </div>
    </section>

    {/* ══ 3 · TESTING FOR PERFECT EQUILIBRIUM ══ */}
    <section className="bg-primary-foreground py-16 md:py-24 lg:py-28">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div {...fadeUp} className="mb-10 md:mb-14 max-w-3xl">
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3 flex items-center gap-2">
            <span className="w-7 h-px bg-primary inline-block" />
            Moisture Science
          </p>
          <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-foreground leading-[1.1] mb-5">
            Testing for Perfect{" "}
            <em className="italic font-normal text-primary">Equilibrium.</em>
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-muted-foreground leading-[1.85]">
            We use advanced NWFA protocols to measure the exact moisture difference between your new hardwood and your subfloor before a single plank is installed.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              icon: Ruler,
              title: "Strip Flooring (Under 3\")",
              subtitle: "Strip Flooring Tolerances",
              desc: "For traditional strip floors measuring 3 inches or less in width, industry standards dictate that the moisture content of the hardwood must be strictly within 4% of the subfloor's moisture content before installation can safely begin.",
            },
            {
              icon: Layers,
              title: "Wide Plank Physics (Over 3\")",
              subtitle: "Wide Plank Tolerances",
              desc: "The physics are simple: the larger the piece of wood, the greater its capacity to expand and contract. Wide planks demand significantly tighter moisture tolerances between the wood and the subfloor to prevent severe cupping and seasonal gapping.",
            },
            {
              icon: Gauge,
              title: "The Specific Gravity Standard",
              subtitle: "Specific Gravity Calibration",
              desc: "Different wood species have different densities. Standard meters cannot tell the difference between White Oak and Brazilian Walnut. We utilize professional meters with specific gravity adjustments to calibrate for the exact species, and insulated pin meters to test internal core layers for scientifically flawless data.",
            },
          ].map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="border border-border/50 rounded-2xl p-7 md:p-8 flex flex-col group hover:border-primary/30 hover:bg-muted/50 hover:shadow-[0_8px_30px_-12px_hsl(var(--gold-500)/0.15)] hover:-translate-y-1 transition-all duration-500"
            >
              <card.icon className="w-7 h-7 text-primary mb-5 group-hover:text-[hsl(var(--gold-300))] transition-colors duration-500" strokeWidth={1.5} />
              <h4 className="font-display text-lg md:text-xl font-semibold text-foreground mb-1 leading-snug">
                {card.title}
              </h4>
              <p className="text-[11px] tracking-wider uppercase text-primary/70 mb-3">{card.subtitle}</p>
              <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-relaxed group-hover:text-foreground/70 transition-colors duration-500">
                {card.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ══ 4 · THE SCIENCE OF MOVEMENT ══ */}
    <section className="bg-foreground py-16 md:py-24 lg:py-28">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div {...fadeUp} className="mb-10 md:mb-14 max-w-3xl">
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3 flex items-center gap-2">
            <span className="w-7 h-px bg-primary inline-block" />
            Wood Science
          </p>
          <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-primary-foreground leading-[1.1] mb-5">
            Predicting the Physics of{" "}
            <em className="italic font-normal text-[hsl(var(--gold-300))]">Your Floor.</em>
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/55 leading-[1.85]">
            Every floor behaves differently based on three distinct scientific factors.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              icon: BarChart3,
              title: "The EMC Chart",
              subtitle: "Temperature + Humidity",
              desc: (
                <>
                  We don't guess how your floor will react. By using an industry-standard <strong className="text-[hsl(var(--gold-300))] font-medium">Temperature and Humidity Chart</strong>, we can measure your home's current climate and accurately predict exactly what moisture percentage your wood will reach. This sets your entire project up for long-term success.
                </>
              ),
              link: null,
            },
            {
              icon: Thermometer,
              title: "Dimensional Change Coefficient",
              subtitle: "Species Behavior",
              desc: (
                <>
                  Not all woods are created equal. Different species shrink and swell at different rates when exposed to humidity a metric known as the <strong className="text-[hsl(var(--gold-300))] font-medium">Dimensional Change Coefficient</strong>. For example, Hickory will move significantly more than a highly stable species like White Oak.
                </>
              ),
              link: null,
            },
            {
              icon: Layers,
              title: "Cut & Core Construction",
              subtitle: "Structural Engineering",
              desc: (
                <>
                  How the lumber is cut (<strong className="text-[hsl(var(--gold-300))] font-medium">Quarter-Sawn vs. Plain-Sawn</strong>) and how the board is constructed (<strong className="text-[hsl(var(--gold-300))] font-medium">Solid vs. Engineered</strong>) dramatically alters how much the floor will expand or contract.
                </>
              ),
              link: "/product-specification",
            },
          ].map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="border border-primary-foreground/10 rounded-2xl p-7 md:p-8 flex flex-col group hover:border-primary/30 hover:bg-primary-foreground/[.03] transition-all duration-500"
            >
              <card.icon className="w-7 h-7 text-primary mb-5 group-hover:text-[hsl(var(--gold-300))] transition-colors duration-500" strokeWidth={1.5} />
              <h4 className="font-display text-lg md:text-xl font-semibold text-primary-foreground mb-1 leading-snug">
                {card.title}
              </h4>
              <p className="text-[11px] tracking-wider uppercase text-primary/70 mb-3">{card.subtitle}</p>
              <p className="text-[12px] md:text-[13px] font-light text-primary-foreground/50 leading-relaxed group-hover:text-primary-foreground/65 transition-colors duration-500 flex-1">
                {card.desc}
              </p>
              {card.link && (
                <Link
                  to={card.link}
                  className="mt-5 text-xs font-medium text-primary hover:text-[hsl(var(--gold-300))] transition-colors inline-flex items-center gap-1"
                >
                  Read our Specification Guide →
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ══ 5 · MICHIGAN CLIMATE & HVAC ══ */}
    <section className="bg-primary-foreground py-16 md:py-24 lg:py-28">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div {...fadeUp} className="mb-10 md:mb-14 max-w-3xl">
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3 flex items-center gap-2">
            <span className="w-7 h-px bg-primary inline-block" />
            Climate Science
          </p>
          <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-foreground leading-[1.1] mb-5">
            Controlling the{" "}
            <em className="italic font-normal text-primary">Climate.</em>
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-muted-foreground leading-[1.85]">
            Michigan's humid summers and bone-dry winters will destroy a floor if your home's thermal envelope isn't managed. Your HVAC system is the life support for your hardwood.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              icon: Thermometer,
              title: "Winter Shrinkage",
              subtitle: "Low Humidity",
              desc: "When the furnace runs all winter, it strips moisture from the air. The wood dries out, shrinks, and causes ugly gaps between planks. A whole-home humidifier is mandatory to maintain the NWFA-required 30 to 50% relative humidity.",
            },
            {
              icon: Droplets,
              title: "Summer Expansion",
              subtitle: "High Humidity",
              desc: "During humid Michigan summers, wood absorbs moisture from the air and expands. If the air conditioning isn't controlling the indoor humidity, the planks will crush against each other, causing severe cupping and structural buckling.",
            },
            {
              icon: Power,
              title: "The 'Always On' Rule",
              subtitle: "Climate Stability",
              desc: "Your home's HVAC system must be fully operational for at least 5 days before the wood is delivered, maintaining a temperature between 60 to 80°F. The environment must be controlled before, during, and for the entire life of the floor.",
            },
          ].map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="border border-border/50 rounded-2xl p-7 md:p-8 flex flex-col group hover:border-primary/30 hover:bg-muted/50 transition-all duration-500"
            >
              <card.icon className="w-7 h-7 text-primary mb-5 group-hover:text-[hsl(var(--gold-300))] transition-colors duration-500" strokeWidth={1.5} />
              <h4 className="font-display text-lg md:text-xl font-semibold text-foreground mb-1 leading-snug">
                {card.title}
              </h4>
              <p className="text-[11px] tracking-wider uppercase text-primary/70 mb-3">{card.subtitle}</p>
              <p className="text-[12px] md:text-[13px] font-light text-muted-foreground leading-relaxed group-hover:text-foreground/70 transition-colors duration-500">
                {card.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ══ 6 · HEATING SYSTEM CHALLENGE ══ */}
    <section className="bg-foreground py-16 md:py-24 lg:py-28">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div {...fadeUp} className="mb-10 md:mb-14 max-w-3xl">
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3 flex items-center gap-2">
            <span className="w-7 h-px bg-primary inline-block" />
            Heating Diagnostics
          </p>
          <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-primary-foreground leading-[1.1] mb-5">
            Diagnosing Your{" "}
            <em className="italic font-normal text-[hsl(var(--gold-300))]">Heating System.</em>
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/55 leading-[1.85]">
            How your home is heated completely dictates your humidification strategy. Not all heating systems are equipped to protect a hardwood floor.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="border border-primary-foreground/10 rounded-2xl p-7 md:p-8 group hover:border-primary/30 hover:bg-primary-foreground/[.03] transition-all duration-500"
          >
            <Flame className="w-7 h-7 text-primary mb-5 group-hover:text-[hsl(var(--gold-300))] transition-colors duration-500" strokeWidth={1.5} />
            <h4 className="font-display text-lg md:text-xl font-semibold text-primary-foreground mb-1 leading-snug">
              Radiant &amp; Boiler Heat
            </h4>
            <p className="text-[11px] tracking-wider uppercase text-primary/70 mb-3">No Ductwork Systems</p>
            <p className="text-[12px] md:text-[13px] font-light text-primary-foreground/50 leading-relaxed group-hover:text-primary-foreground/65 transition-colors duration-500">
              Systems like in-floor radiant heat or traditional boiler radiators are incredibly efficient, but they lack forced ductwork. This means they have absolutely no built-in way to deliver humidity to the air. For these homes, we strongly recommend installing a separate, ducted <strong className="text-[hsl(var(--gold-300))] font-medium">steam humidification unit</strong> if architecture allows, or utilizing strategically placed, high-capacity portable humidifiers to maintain the required 30–50% RH.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="border border-primary-foreground/10 rounded-2xl p-7 md:p-8 group hover:border-primary/30 hover:bg-primary-foreground/[.03] transition-all duration-500"
          >
            <Wind className="w-7 h-7 text-primary mb-5 group-hover:text-[hsl(var(--gold-300))] transition-colors duration-500" strokeWidth={1.5} />
            <h4 className="font-display text-lg md:text-xl font-semibold text-primary-foreground mb-1 leading-snug">
              Forced Air &amp; Flow-Through Units
            </h4>
            <p className="text-[11px] tracking-wider uppercase text-primary/70 mb-3">Ducted Systems</p>
            <p className="text-[12px] md:text-[13px] font-light text-primary-foreground/50 leading-relaxed group-hover:text-primary-foreground/65 transition-colors duration-500">
              Standard furnaces often feature <strong className="text-[hsl(var(--gold-300))] font-medium">flow-through humidifiers</strong>. While efficient, the dial on these units is not a guarantee. Just because you set the dial to "40%" or "Winter" does not mean your home is actually holding that humidity. Variables like vaulted ceilings, drafty windows, and the overall insulation value of your <strong className="text-[hsl(var(--gold-300))] font-medium">thermal envelope</strong> drastically impact how much moisture is actually retained in the air.
            </p>
          </motion.div>
        </div>
      </div>
    </section>

    {/* ══ 7 · HYGROMETER CALLOUT ══ */}
    <section className="bg-primary-foreground py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div
          {...fadeUp}
          className="border border-primary/20 bg-primary/[.04] rounded-2xl p-8 md:p-12 max-w-4xl mx-auto"
        >
          <div className="flex items-start gap-4 mb-5">
            <Gauge className="w-7 h-7 text-[hsl(var(--gold-300))] shrink-0 mt-1" strokeWidth={1.5} />
            <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary">
              Essential Equipment
            </p>
          </div>
          <h2 className="font-display text-[clamp(24px,4vw,42px)] font-semibold text-foreground leading-[1.1] mb-5">
            Trust, But Verify:{" "}
            <em className="italic font-normal text-primary">The Hygrometer.</em>
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-muted-foreground leading-[1.9]">
            You cannot manage what you do not measure. You must monitor your home's environment independently of your furnace settings. We advise every hardwood floor owner to invest in a quality digital <strong className="text-primary font-medium">hygrometer</strong> (an indoor humidity monitor). Placing these in the rooms with your hardwood allows you to verify that your HVAC system is actually capable of maintaining the correct environment for your floor.
          </p>
        </motion.div>
      </div>
    </section>

    {/* ══ 8 · VACATION RULE WARNING ══ */}
    <section className="bg-foreground py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div
          {...fadeUp}
          className="border border-[hsl(var(--gold-300)/0.3)] bg-[hsl(var(--gold-900)/0.15)] rounded-2xl p-8 md:p-12 max-w-4xl mx-auto"
        >
          <div className="flex items-start gap-4 mb-5">
            <Plane className="w-7 h-7 text-[hsl(var(--gold-300))] shrink-0 mt-1" strokeWidth={1.5} />
            <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-300))]">
              Critical Warning
            </p>
          </div>
          <h2 className="font-display text-[clamp(24px,4vw,42px)] font-semibold text-primary-foreground leading-[1.1] mb-5">
            The #1 Cause of Winter Failures:{" "}
            <em className="italic font-normal text-[hsl(var(--gold-300))]">The Vacation Rule.</em>
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/55 leading-[1.9]">
            When homeowners travel south for the winter or go on extended vacations, they often turn their thermostats down to save energy. <strong className="text-[hsl(var(--gold-300))] font-medium">Never turn off your climate control.</strong> Your hardwood floor is a living investment. You must maintain the exact same temperature and humidity environment as if you were actively living there. Dropping the temperature and turning off the humidifier while you are away is a guaranteed recipe for severe gapping, checking, and total warranty voidance.
          </p>
        </motion.div>
      </div>
    </section>

    {/* ══ 9 · CTA BANNER ══ */}
    <section className="bg-[hsl(var(--gold-900))] py-16 md:py-20">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12 text-center">
        <motion.div {...fadeUp}>
          <h2 className="font-display text-[clamp(24px,4vw,42px)] font-semibold text-primary-foreground leading-[1.1] mb-4">
            Don't let your home's environment{" "}
            <em className="italic font-normal text-[hsl(var(--gold-300))]">destroy your investment.</em>
          </h2>
          <p className="text-[13px] md:text-[15px] font-light text-primary-foreground/50 leading-[1.85] max-w-2xl mx-auto mb-8">
            Contact our NWFA Certified Inspectors to measure your subfloor, evaluate your HVAC, and engineer a floor that will last a lifetime.
          </p>
          <QuoteModal
            trigger={
              <button className="bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors">
                Request an Environmental Evaluation
              </button>
            }
          />
        </motion.div>
      </div>
    </section>

    <RelatedServices
      heading="Now that you understand the science."
      subheading="Related Services"
      links={[
        { title: "Hardwood Installation", desc: "Proper acclimation is step one. Our NWFA Certified Installers handle the precision installation that follows.", to: "/installation", tag: "Service" },
        { title: "Subfloor Leveling & Repair", desc: "Moisture starts at the subfloor. We engineer flatness and moisture barriers before any wood is laid.", to: "/subfloor-leveling", tag: "Service" },
        { title: "Dust Control Refinishing", desc: "Existing floors showing seasonal gaps? Our refinishing process addresses both the finish and the environment.", to: "/refinishing", tag: "Service" },
        { title: "NWFA Certified Inspections", desc: "Suspect environmental damage? Our forensic inspections prove whether moisture caused the failure.", to: "/inspections", tag: "Service" },
        { title: "MOSO Bamboo", desc: "Bamboo requires the same forensic acclimation as hardwood. See our 'Egg-Crate' lattice-stacking method.", to: "/brands/ironwood", tag: "Brand" },
        { title: "Hardwood Flooring in Ann Arbor", desc: "Ann Arbor's older homes near the Huron River demand expert moisture mitigation. See our local approach.", to: "/hardwood-flooring/ann-arbor-mi", tag: "City" },
      ]}
    />

    </main>
    <SiteFooter />
  </>
  );
};

export default EnvironmentAcclimation;
